import React, { useState, useCallback, useEffect } from 'react';
import './TableSetupDialog.css';
import { Seat } from '../../interfaces/mealPlan';
import { ProstayGuestDto, Customer } from '../../interfaces';
import { CombinedSearchResult } from '../../Toolbar';

function formatDate(dateStr?: string): string {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

interface TableSetupDialogProps {
    tableName: string;
    defaultSeats: number;
    existingSeats?: Seat[];
    /** Why the dialog is open, when it is not simply someone tapping a table. */
    contextNote?: string;
    pendingGuest?: CombinedSearchResult | null;
    onPendingGuestConsumed?: () => void;
    onConfirm: (seats: Seat[], walkInCustomers: Customer[]) => void;
    onCancel: () => void;
}

interface RoomGroup {
    roomNumber: string;
    reservationId: string;
    guests: ProstayGuestDto[];
    rateName?: string;
}

interface SeatSetup {
    id: string;
    seatNumber: number;
    type: 'hotel_guest' | 'walk_in';
    guestName: string;
    roomNumber: string;
    selectedGuest: ProstayGuestDto | null;
    searchTerm: string;
    searchResults: ProstayGuestDto[];
    roomGroups: RoomGroup[];
    showSearch: boolean;
    searching: boolean;
    // Walk-in customer fields
    selectedCustomerId: string | null;
    showCustomerSearch: boolean;
    forceCreateNew: boolean;
}

const customerLabel = (c: Customer): string => {
    const name = `${c.firstName || ''} ${c.lastName || ''}`.trim();
    return name || c.email || 'Unnamed';
};

const splitName = (full: string): { firstName: string; lastName: string } => {
    const trimmed = full.trim();
    if (!trimmed) return { firstName: '', lastName: '' };
    const parts = trimmed.split(/\s+/);
    return {
        firstName: parts[0],
        lastName: parts.slice(1).join(' '),
    };
};

const TableSetupDialog: React.FC<TableSetupDialogProps> = ({ tableName, defaultSeats, contextNote, existingSeats, pendingGuest, onPendingGuestConsumed, onConfirm, onCancel }) => {
    const initSeats = (): SeatSetup[] => {
        if (existingSeats && existingSeats.length > 0) {
            return existingSeats.map((s, i) => ({
                id: s.id,
                seatNumber: s.seatNumber || i + 1,
                type: s.type,
                guestName: s.guestName || '',
                roomNumber: s.roomNumber || '',
                selectedGuest: s.type === 'hotel_guest' ? {
                    id: s.reservationId || '',
                    firstName: (s.guestName || '').split(' ')[0] || '',
                    lastName: (s.guestName || '').split(' ').slice(1).join(' ') || '',
                    reservationId: s.reservationId || '',
                    reservationNumber: s.reservationNumber,
                    roomNumber: s.roomNumber,
                    roomWithSttausId: s.roomWithStatusId,
                    rateId: s.rateId,
                    rateName: s.rateName,
                    reservationCheckinDate: s.checkInDate,
                    reservationCheckoutDate: s.checkOutDate,
                    isRepeat: false,
                    repeatGuests: false,
                } as ProstayGuestDto : null,
                searchTerm: s.type === 'hotel_guest' ? `${s.guestName}${s.roomNumber ? ` (Room ${s.roomNumber})` : ''}` : '',
                searchResults: [],
                showSearch: false,
                searching: false,
                selectedCustomerId: s.customerId || null,
                showCustomerSearch: false,
                forceCreateNew: false,
            }));
        }
        return Array.from({ length: Math.max(defaultSeats, 1) }, (_, i) => ({
            id: `seat-${Date.now()}-${i}`,
            seatNumber: i + 1,
            type: 'walk_in' as const,
            guestName: '',
            roomNumber: '',
            selectedGuest: null,
            searchTerm: '',
            searchResults: [],
            showSearch: false,
            searching: false,
            selectedCustomerId: null,
            showCustomerSearch: false,
            forceCreateNew: false,
        }));
    };
    const [seatCount, setSeatCount] = useState(existingSeats?.length || Math.max(defaultSeats, 1));
    const [seats, setSeats] = useState<SeatSetup[]>(initSeats);
    const [customers, setCustomers] = useState<Customer[]>([]);
    const [savingCustomers, setSavingCustomers] = useState(false);
    const [ambiguousSeatIds, setAmbiguousSeatIds] = useState<Set<string>>(new Set());

    useEffect(() => {
        if (!pendingGuest) return;
        setSeats(prev => {
            // Determine if we can fill the first "empty" seat, otherwise append a new one.
            const isEmptyWalkIn = (s: SeatSetup) => s.type === 'walk_in' && !s.guestName.trim() && !s.selectedCustomerId && !s.forceCreateNew;
            const isEmptyHotel = (s: SeatSetup) => s.type === 'hotel_guest' && !s.selectedGuest;
            const targetIdx = prev.findIndex(s => isEmptyWalkIn(s) || isEmptyHotel(s));

            const buildFilled = (base: SeatSetup, nextSeatNum: number): SeatSetup => {
                if (pendingGuest.type === 'prostay' && pendingGuest.prostayGuest) {
                    const g = pendingGuest.prostayGuest;
                    return {
                        ...base,
                        seatNumber: base.seatNumber || nextSeatNum,
                        type: 'hotel_guest',
                        guestName: `${g.firstName || ''} ${g.lastName || ''}`.trim(),
                        roomNumber: g.roomNumber || '',
                        selectedGuest: g,
                        searchTerm: `${g.firstName || ''} ${g.lastName || ''}${g.roomNumber ? ` (Room ${g.roomNumber})` : ''}`.trim(),
                        searchResults: [],
                        showSearch: false,
                        searching: false,
                        selectedCustomerId: null,
                        showCustomerSearch: false,
                        forceCreateNew: false,
                    };
                }
                if (pendingGuest.type === 'tableview' && pendingGuest.customer) {
                    const c = pendingGuest.customer;
                    return {
                        ...base,
                        seatNumber: base.seatNumber || nextSeatNum,
                        type: 'walk_in',
                        guestName: customerLabel(c),
                        roomNumber: '',
                        selectedGuest: null,
                        searchTerm: '',
                        searchResults: [],
                        showSearch: false,
                        searching: false,
                        selectedCustomerId: c.id,
                        showCustomerSearch: false,
                        forceCreateNew: false,
                    };
                }
                return base;
            };

            let next: SeatSetup[];
            if (targetIdx >= 0) {
                next = [...prev];
                next[targetIdx] = buildFilled(prev[targetIdx], prev[targetIdx].seatNumber);
            } else {
                const newBase: SeatSetup = {
                    id: `seat-${Date.now()}`,
                    seatNumber: prev.length + 1,
                    type: 'walk_in',
                    guestName: '',
                    roomNumber: '',
                    selectedGuest: null,
                    searchTerm: '',
                    searchResults: [],
                    showSearch: false,
                    searching: false,
                    selectedCustomerId: null,
                    showCustomerSearch: false,
                    forceCreateNew: false,
                };
                next = [...prev, buildFilled(newBase, prev.length + 1)];
                setSeatCount(next.length);
            }
            return next;
        });
        onPendingGuestConsumed?.();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingGuest]);

    useEffect(() => {
        const fetchCustomers = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/Customer/`, {
                    method: 'GET',
                    credentials: 'include',
                });
                if (!response.ok) return;
                const data = await response.json();
                setCustomers(data || []);
            } catch (error) {
                console.error('Error fetching customers:', error);
            }
        };
        fetchCustomers();
    }, []);

    const searchGuestsFromApi = useCallback(async (searchTerm: string): Promise<ProstayGuestDto[]> => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/Prostay/search-prostay-guest`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    currentPage: 1,
                    searchTerm: searchTerm,
                    pageSize: 50,
                    filterDuplicatesByEmail: false
                }),
                credentials: 'include',
            });
            const data = await response.json();
            return data.data || [];
        } catch (error) {
            console.error('Error searching guests:', error);
            return [];
        }
    }, []);

    const updateSeatCount = (count: number) => {
        const newCount = Math.max(1, Math.min(20, count));
        setSeatCount(newCount);
        if (newCount > seats.length) {
            const newSeats = Array.from({ length: newCount - seats.length }, (_, i) => ({
                id: `seat-${Date.now()}-${seats.length + i}`,
                seatNumber: seats.length + i + 1,
                type: 'walk_in' as const,
                guestName: '',
                roomNumber: '',
                selectedGuest: null,
                searchTerm: '',
                searchResults: [],
                roomGroups: [],
                showSearch: false,
                searching: false,
                selectedCustomerId: null,
                showCustomerSearch: false,
                forceCreateNew: false,
            }));
            setSeats([...seats, ...newSeats]);
        } else {
            setSeats(seats.slice(0, newCount));
        }
    };

    const updateSeat = (index: number, updates: Partial<SeatSetup>) => {
        setSeats(prev => {
            const newSeats = [...prev];
            newSeats[index] = { ...newSeats[index], ...updates };
            return newSeats;
        });
    };

    const toggleSeatType = (index: number) => {
        const seat = seats[index];
        const newType = seat.type === 'hotel_guest' ? 'walk_in' : 'hotel_guest';
        updateSeat(index, {
            type: newType,
            guestName: '',
            roomNumber: '',
            selectedGuest: null,
            searchTerm: '',
            searchResults: [],
            showSearch: false,
            searching: false,
            selectedCustomerId: null,
            showCustomerSearch: false,
            forceCreateNew: false,
        });
    };

    const selectCustomer = (index: number, customer: Customer) => {
        updateSeat(index, {
            selectedCustomerId: customer.id,
            guestName: customerLabel(customer),
            showCustomerSearch: false,
            forceCreateNew: false,
        });
    };

    const markCreateNew = (index: number) => {
        updateSeat(index, {
            selectedCustomerId: null,
            showCustomerSearch: false,
            forceCreateNew: true,
        });
    };

    const clearSelectedCustomer = (index: number) => {
        updateSeat(index, { selectedCustomerId: null, forceCreateNew: false });
    };

    const filterCustomers = (term: string): Customer[] => {
        const t = term.trim().toLowerCase();
        if (!t) return customers.slice(0, 20);
        return customers.filter(c =>
            (c.firstName && c.firstName.toLowerCase().includes(t)) ||
            (c.lastName && c.lastName.toLowerCase().includes(t)) ||
            (c.email && c.email.toLowerCase().includes(t)) ||
            customerLabel(c).toLowerCase().includes(t)
        ).slice(0, 20);
    };

    const createCustomer = async (fullName: string): Promise<string | null> => {
        try {
            const { firstName, lastName } = splitName(fullName);
            const response = await fetch(`${process.env.REACT_APP_API_URL}/Customer`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    id: '00000000-0000-0000-0000-000000000000',
                    propertyId: '00000000-0000-0000-0000-000000000000',
                    firstName,
                    lastName,
                    notes: [],
                    history: [],
                }),
                credentials: 'include',
            });
            if (!response.ok) return null;
            const data = await response.json();
            return data?.id || null;
        } catch (error) {
            console.error('Error creating customer:', error);
            return null;
        }
    };

    const fetchReservationGuests = useCallback(async (reservationId: string): Promise<ProstayGuestDto[]> => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/Prostay/reservation-guests/${reservationId}`, {
                method: 'GET',
                credentials: 'include',
            });
            if (!response.ok) return [];
            return await response.json();
        } catch {
            return [];
        }
    }, []);

    const triggerSearch = async (index: number, value: string) => {
        updateSeat(index, { showSearch: true, searching: true });
        const results = await searchGuestsFromApi(value);

        // For each unique reservation in results, fetch ALL guests from that reservation
        const resIds = [...new Set(results.filter(g => g.reservationId).map(g => g.reservationId))];

        const roomGroups: RoomGroup[] = [];
        const seenRoomKeys = new Set<string>();

        await Promise.all(resIds.map(async (resId) => {
            const allGuests = await fetchReservationGuests(resId);
            // Group by room
            const byRoom = new Map<string, ProstayGuestDto[]>();
            for (const g of allGuests) {
                if (!g.roomNumber) continue;
                const key = `${g.reservationId}|${g.roomNumber}`;
                if (!byRoom.has(key)) byRoom.set(key, []);
                byRoom.get(key)!.push(g);
            }
            for (const [key, guests] of byRoom) {
                if (guests.length > 1 && !seenRoomKeys.has(key)) {
                    seenRoomKeys.add(key);
                    roomGroups.push({
                        roomNumber: guests[0].roomNumber!,
                        reservationId: guests[0].reservationId,
                        guests,
                        rateName: guests[0].rateName,
                    });
                }
            }
        }));

        updateSeat(index, { searchResults: results, roomGroups, searching: false });
    };

    const selectGuest = (index: number, guest: ProstayGuestDto) => {
        updateSeat(index, {
            selectedGuest: guest,
            guestName: `${guest.firstName} ${guest.lastName}`,
            roomNumber: guest.roomNumber || '',
            searchTerm: `${guest.firstName} ${guest.lastName}${guest.roomNumber ? ` (Room ${guest.roomNumber})` : ''}`,
            showSearch: false,
        });
    };

    const selectRoom = (index: number, roomGuests: ProstayGuestDto[]) => {
        setSeats(prev => {
            const next = [...prev];
            let seatIdx = index;
            for (const guest of roomGuests) {
                if (seatIdx >= next.length) {
                    // Add a new seat if needed
                    next.push({
                        id: `seat-${Date.now()}-${next.length}`,
                        seatNumber: next.length + 1,
                        type: 'hotel_guest',
                        guestName: '',
                        roomNumber: '',
                        selectedGuest: null,
                        searchTerm: '',
                        searchResults: [],
                        showSearch: false,
                        searching: false,
                        selectedCustomerId: null,
                        showCustomerSearch: false,
                        forceCreateNew: false,
                    });
                }
                next[seatIdx] = {
                    ...next[seatIdx],
                    type: 'hotel_guest',
                    selectedGuest: guest,
                    guestName: `${guest.firstName} ${guest.lastName}`,
                    roomNumber: guest.roomNumber || '',
                    searchTerm: `${guest.firstName} ${guest.lastName}${guest.roomNumber ? ` (Room ${guest.roomNumber})` : ''}`,
                    searchResults: [],
                    showSearch: false,
                    searching: false,
                };
                seatIdx++;
            }
            setSeatCount(next.length);
            return next;
        });
    };

    const handleConfirm = async () => {
        // Pre-check: any walk-in seat whose typed name matches >1 existing
        // customers and that hasn't been explicitly resolved is ambiguous.
        const ambiguous = new Set<string>();
        for (const s of seats) {
            if (s.type !== 'walk_in' || s.selectedCustomerId || s.forceCreateNew || !s.guestName.trim()) continue;
            const typed = s.guestName.trim().toLowerCase();
            const matches = customers.filter(c => customerLabel(c).toLowerCase() === typed);
            if (matches.length > 1) ambiguous.add(s.id);
        }
        if (ambiguous.size > 0) {
            setAmbiguousSeatIds(ambiguous);
            // Open the dropdown for the affected seats so the user can pick.
            setSeats(prev => prev.map(s => ambiguous.has(s.id) ? { ...s, showCustomerSearch: true } : s));
            return;
        }
        setAmbiguousSeatIds(new Set());

        setSavingCustomers(true);
        try {
            const resolvedSeats = await Promise.all(seats.map(async s => {
                let customerId = s.selectedCustomerId;
                if (s.type === 'walk_in' && !customerId && s.guestName.trim()) {
                    if (s.forceCreateNew) {
                        customerId = await createCustomer(s.guestName.trim());
                    } else {
                        const typed = s.guestName.trim().toLowerCase();
                        const matches = customers.filter(c => customerLabel(c).toLowerCase() === typed);
                        if (matches.length === 1) {
                            customerId = matches[0].id;
                        } else if (matches.length === 0) {
                            customerId = await createCustomer(s.guestName.trim());
                        }
                        // matches.length > 1 is blocked above; should not reach here.
                    }
                }
                return { seat: s, customerId };
            }));

            const walkInCustomers: Customer[] = [];
            const seenCustomerIds = new Set<string>();
            for (const { customerId } of resolvedSeats) {
                if (!customerId || seenCustomerIds.has(customerId)) continue;
                const match = customers.find(c => c.id === customerId);
                if (match) {
                    walkInCustomers.push(match);
                    seenCustomerIds.add(customerId);
                }
            }

            const finalSeats: Seat[] = resolvedSeats.map(({ seat: s, customerId }) => ({
                id: s.id,
                seatNumber: s.seatNumber,
                type: s.type,
                guestName: s.type === 'hotel_guest' ? s.guestName : (s.guestName || `Guest ${s.seatNumber}`),
                customerId: s.type === 'walk_in' ? (customerId || undefined) : undefined,
                roomNumber: s.selectedGuest?.roomNumber,
                roomWithStatusId: s.selectedGuest?.roomWithSttausId,
                reservationNumber: s.selectedGuest?.reservationNumber,
                reservationId: s.selectedGuest?.reservationId,
                rateId: s.selectedGuest?.rateId,
                rateName: s.selectedGuest?.rateName,
                checkInDate: s.selectedGuest?.reservationCheckinDate,
                checkOutDate: s.selectedGuest?.reservationCheckoutDate,
            }));
            onConfirm(finalSeats, walkInCustomers);
        } finally {
            setSavingCustomers(false);
        }
    };

    const canConfirm = seats.every(s =>
        s.type === 'walk_in' || (s.type === 'hotel_guest' && s.selectedGuest)
    );

    return (
        <div className='ps-scope tsd-overlay'>
            <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
            <div className='tsd-card' role='dialog' aria-modal='true'>
                {/* Header */}
                <div className='tsd-head'>
                    <div className='tsd-headtop'>
                        <div>
                            <h5 className='tsd-title'>Table Setup - {tableName}</h5>
                            {/* Says why the dialog opened when it was not just a tap on the
                              * table - seating a booking arrives here with the guest already
                              * filled in, which is confusing without a reason on screen. */}
                            {contextNote && <span className='tsd-context'>{contextNote}</span>}
                            <p className='tsd-sub'>Set up guests for this table</p>
                        </div>
                        <button className='tsd-close' onClick={onCancel} aria-label='Close'>
                            <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
                                <path d="M13 1L1 13M1 1L13 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>
                    </div>

                    <div className='tsd-stepper'>
                        <span className='tsd-stepper__label'>Number of persons:</span>
                        <div className='tsd-stepper__controls'>
                            <button
                                className='tsd-step'
                                onClick={() => updateSeatCount(seatCount - 1)}
                            >-</button>
                            <span className='tsd-count'>{seatCount}</span>
                            <button
                                className='tsd-step'
                                onClick={() => updateSeatCount(seatCount + 1)}
                            >+</button>
                        </div>
                    </div>
                </div>

                {/* Seat list */}
                <div className='tsd-body'>
                    {seats.map((seat, idx) => (
                        <div key={seat.id} className={`tsd-seat${seat.type === 'hotel_guest' ? ' tsd-seat--hotel' : ''}`}>
                            <div className='tsd-seat__head'>
                                <span className='tsd-seat__label'>
                                    Seat {seat.seatNumber}
                                </span>
                                <div className='tsd-seg'>
                                    <button
                                        className={`tsd-segbtn${seat.type === 'hotel_guest' ? ' is-on' : ''}`}
                                        onClick={() => { if (seat.type !== 'hotel_guest') toggleSeatType(idx); }}
                                    >Hotel Guest</button>
                                    <button
                                        className={`tsd-segbtn${seat.type === 'walk_in' ? ' is-on' : ''}`}
                                        onClick={() => { if (seat.type !== 'walk_in') toggleSeatType(idx); }}
                                    >Walk-in</button>
                                </div>
                            </div>

                            {seat.type === 'hotel_guest' ? (
                                <div>
                                    <div className='tsd-field'>
                                        <input
                                            type="text"
                                            value={seat.searchTerm}
                                            onChange={(e) => updateSeat(idx, { searchTerm: e.target.value })}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    triggerSearch(idx, seat.searchTerm);
                                                }
                                            }}
                                            placeholder="Search room or guest name, press Enter..."
                                            className="tsd-input"
                                            style={{ paddingRight: seat.searchTerm ? 64 : 36 }}
                                        />
                                        {seat.searchTerm && (
                                            <button
                                                type="button"
                                                className='tsd-clearbtn'
                                                onClick={() => updateSeat(idx, {
                                                    searchTerm: '',
                                                    guestName: '',
                                                    roomNumber: '',
                                                    selectedGuest: null,
                                                    searchResults: [],
                                                    showSearch: false,
                                                })}
                                                aria-label="Clear"
                                                style={{ right: 32, top: '50%', transform: 'translateY(-50%)' }}
                                            >
                                                <svg width="10" height="10" viewBox="0 0 14 14" fill="none">
                                                    <path d="M13 1L1 13M1 1L13 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            </button>
                                        )}
                                        <button
                                            className='tsd-searchbtn'
                                            onClick={() => triggerSearch(idx, seat.searchTerm)}
                                            disabled={seat.searching}
                                        >
                                            {seat.searching ? (
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ animation: 'spin 1s linear infinite' }}>
                                                    <circle cx="12" cy="12" r="10" stroke="#1643E0" strokeWidth="2" strokeDasharray="31.4 31.4" strokeLinecap="round" />
                                                </svg>
                                            ) : (
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <circle cx="11" cy="11" r="8"/>
                                                    <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                                                </svg>
                                            )}
                                        </button>
                                    </div>
                                    {seat.showSearch && seat.searchResults.length > 0 && (
                                        <div className='tsd-results'>
                                            {/* Room group options — fill all guests from a room at once */}
                                            {(seat.roomGroups || []).map(group => (
                                                <div
                                                    key={`room-${group.reservationId}-${group.roomNumber}`}
                                                    className='tsd-result tsd-result--room'
                                                    onClick={() => selectRoom(idx, group.guests)}
                                                >
                                                    <div className='d-flex justify-content-between align-items-center'>
                                                        <div className='d-flex align-items-center gap-2'>
                                                            <span style={{
                                                                fontSize: 11, fontWeight: 700, padding: '2px 8px',
                                                                borderRadius: 10, backgroundColor: 'var(--color-primary, #1643E0)', color: '#fff',
                                                            }}>Room {group.roomNumber}</span>
                                                            <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--color-primary, #1643E0)' }}>
                                                                {group.guests.length} guests
                                                            </span>
                                                        </div>
                                                        {group.rateName && (
                                                            <span style={{
                                                                fontSize: 11, fontWeight: 600, padding: '2px 8px',
                                                                borderRadius: 10, backgroundColor: 'var(--color-success-50, #ECFDF3)', color: 'var(--color-success-700, #027A48)',
                                                            }}>
                                                                {group.rateName}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div style={{ fontSize: 11, color: 'var(--color-foreground-muted, #475467)', marginTop: 4 }}>
                                                        {group.guests.map(g => `${g.firstName} ${g.lastName}`).join(', ')}
                                                    </div>
                                                </div>
                                            ))}
                                            {/* Individual guest options */}
                                            {seat.searchResults.map(guest => (
                                                <div
                                                    key={`${guest.reservationNumber}-${guest.id}`}
                                                    className='tsd-result'
                                                    onClick={() => selectGuest(idx, guest)}
                                                    style={{
                                                        backgroundColor: seat.selectedGuest?.id === guest.id ? '#EEF4FF' : '#fff',
                                                    }}
                                                    onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = '#F9FAFB'; }}
                                                    onMouseLeave={(e) => {
                                                        const isSelected = seat.selectedGuest?.id === guest.id;
                                                        (e.currentTarget as HTMLElement).style.backgroundColor = isSelected ? '#EEF4FF' : '#fff';
                                                    }}
                                                >
                                                    <div className='d-flex justify-content-between align-items-center'>
                                                        <div>
                                                            <span style={{ fontWeight: 500, fontSize: 13 }}>
                                                                {guest.firstName} {guest.lastName}
                                                            </span>
                                                            {guest.roomNumber && (
                                                                <span style={{ color: '#667085', fontSize: 12, marginLeft: 8 }}>
                                                                    Room {guest.roomNumber}
                                                                </span>
                                                            )}
                                                        </div>
                                                        {guest.rateName && (
                                                            <span style={{
                                                                fontSize: 11, fontWeight: 600, padding: '2px 8px',
                                                                borderRadius: 10, backgroundColor: '#ECFDF3', color: '#027A48',
                                                            }}>
                                                                {guest.rateName}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div style={{ fontSize: 11, color: '#98A2B3', marginTop: 3, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                                        <span>Booking: <b style={{ color: '#475467' }}>{guest.reservationNumber}</b></span>
                                                        {guest.reservationCheckinDate && (
                                                            <span>In: <b style={{ color: '#475467' }}>{formatDate(guest.reservationCheckinDate)}</b></span>
                                                        )}
                                                        {guest.reservationCheckoutDate && (
                                                            <span>Out: <b style={{ color: '#475467' }}>{formatDate(guest.reservationCheckoutDate)}</b></span>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {seat.selectedGuest && (
                                        <div className='tsd-note tsd-note--ok'>
                                            <span style={{ fontWeight: 500, fontSize: 13 }}>
                                                {seat.selectedGuest.firstName} {seat.selectedGuest.lastName}
                                                {seat.selectedGuest.roomNumber ? ` - Room ${seat.selectedGuest.roomNumber}` : ''}
                                            </span>
                                            {seat.selectedGuest.rateName && (
                                                <span style={{ fontSize: 11, fontWeight: 600 }}>
                                                    {seat.selectedGuest.rateName}
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className='tsd-field'>
                                    <div className='tsd-field__control'>
                                        <input
                                            type="text"
                                            value={seat.guestName}
                                            style={{ paddingRight: 32 }}
                                            onChange={(e) => {
                                                updateSeat(idx, {
                                                    guestName: e.target.value,
                                                    selectedCustomerId: null,
                                                    showCustomerSearch: true,
                                                    forceCreateNew: false,
                                                });
                                                if (ambiguousSeatIds.has(seat.id)) {
                                                    setAmbiguousSeatIds(prev => {
                                                        const next = new Set(prev);
                                                        next.delete(seat.id);
                                                        return next;
                                                    });
                                                }
                                            }}
                                            onFocus={() => updateSeat(idx, { showCustomerSearch: true })}
                                            onBlur={() => setTimeout(() => updateSeat(idx, { showCustomerSearch: false }), 200)}
                                            placeholder="Search customer or enter new name"
                                            className="tsd-input"
                                        />
                                        {seat.guestName && (
                                            <button
                                                type="button"
                                                className='tsd-clearbtn'
                                                onMouseDown={(e) => {
                                                    e.preventDefault();
                                                    updateSeat(idx, {
                                                        guestName: '',
                                                        selectedCustomerId: null,
                                                        forceCreateNew: false,
                                                        showCustomerSearch: false,
                                                    });
                                                    if (ambiguousSeatIds.has(seat.id)) {
                                                        setAmbiguousSeatIds(prev => {
                                                            const next = new Set(prev);
                                                            next.delete(seat.id);
                                                            return next;
                                                        });
                                                    }
                                                }}
                                                aria-label="Clear"
                                                style={{ right: 8, top: '50%', transform: 'translateY(-50%)' }}
                                            >
                                                <svg width="10" height="10" viewBox="0 0 14 14" fill="none">
                                                    <path d="M13 1L1 13M1 1L13 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            </button>
                                        )}
                                    </div>
                                    {ambiguousSeatIds.has(seat.id) && (
                                        <div className='tsd-note tsd-note--err'>
                                            Multiple customers match this name — please pick one from the list.
                                        </div>
                                    )}
                                    {seat.selectedCustomerId && (
                                        <div className='tsd-note tsd-note--ok'>
                                            <span style={{ fontSize: 12, fontWeight: 500 }}>
                                                Existing customer selected
                                            </span>
                                            <button
                                                className='tsd-note__btn'
                                                onClick={() => clearSelectedCustomer(idx)}
                                            >Clear</button>
                                        </div>
                                    )}
                                    {seat.forceCreateNew && !seat.selectedCustomerId && (
                                        <div className='tsd-note tsd-note--warn'>
                                            <span style={{ fontSize: 12, fontWeight: 500 }}>
                                                Will create new customer
                                            </span>
                                            <button
                                                className='tsd-note__btn'
                                                onClick={() => clearSelectedCustomer(idx)}
                                            >Clear</button>
                                        </div>
                                    )}
                                    {seat.showCustomerSearch && !seat.selectedCustomerId && !seat.forceCreateNew && (() => {
                                        const matches = filterCustomers(seat.guestName);
                                        const trimmed = seat.guestName.trim();
                                        if (matches.length === 0 && !trimmed) return null;
                                        return (
                                            <div className='tsd-results tsd-results--float'>
                                                {matches.map(c => (
                                                    <div
                                                        key={c.id}
                                                        className='tsd-result'
                                                        onMouseDown={(e) => { e.preventDefault(); selectCustomer(idx, c); }}
                                                        onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = '#F9FAFB'; }}
                                                        onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = '#fff'; }}
                                                    >
                                                        <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-foreground, #101828)' }}>
                                                            {customerLabel(c)}
                                                        </div>
                                                        {c.email && (
                                                            <div style={{ fontSize: 11, color: 'var(--color-foreground-subtle, #667085)' }}>{c.email}</div>
                                                        )}
                                                    </div>
                                                ))}
                                                {trimmed && (
                                                    <div
                                                        onMouseDown={(e) => { e.preventDefault(); markCreateNew(idx); }}
                                                        style={{
                                                            padding: '8px 12px', cursor: 'pointer',
                                                            backgroundColor: '#FFFAEB',
                                                            borderTop: matches.length > 0 ? '1px solid var(--color-border-subtle, #F2F4F7)' : 'none',
                                                            fontSize: 13, fontWeight: 500, color: '#B54708',
                                                        }}
                                                        onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = '#FEF0C7'; }}
                                                        onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = '#FFFAEB'; }}
                                                    >
                                                        + Create new customer "{trimmed}"
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })()}
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {/* Footer */}
                <div className='tsd-foot'>
                    <button onClick={onCancel} className='tsd-btn tsd-btn--ghost'>Skip Setup</button>
                    <button
                        onClick={handleConfirm}
                        className='tsd-btn tsd-btn--primary'
                        disabled={!canConfirm || savingCustomers}
                    >{savingCustomers ? 'Saving...' : 'Start Order'}</button>
                </div>
            </div>
        </div>
    );
};

export default TableSetupDialog;
